import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Business } from '../types/index'

export const useBusinessStore = defineStore('business', () => {
  const log = useLogger('store:business')
  const { get } = useAuthenticatedFetch()

  // Cookies for critical fields (survive navigation, SSR-compatible)
  const buuidCookie = useCookie('buuid', {
    maxAge: 7 * 24 * 60 * 60,
    secure: true,
    sameSite: 'strict',
  })

  const serviceTypeCookie = useCookie('service_type', {
    maxAge: 7 * 24 * 60 * 60,
    secure: true,
    sameSite: 'strict',
  })

  // Computed refs that read from cookies (reactive, read-only)
  const businessId = computed(() => buuidCookie.value || '')
  const serviceType = computed(() => serviceTypeCookie.value as 'counter' | 'restaurant' | null)

  // State using refs
  const businessInfo = ref<Business | null>(null)
  const isLoading = ref<boolean>(false)
  const error = ref<string | null>(null)
  const isLoggingOut = ref<boolean>(false)
  const hasMultipleBusinesses = ref<boolean>(false)

  // Actions
  const setBusinessId = (id: string): void => {
    if (id && id !== businessId.value) {
      log.info('🏢 Setting business ID:', id)
      buuidCookie.value = id
    }
  }

  const fetchBusinessInfo = async (): Promise<void> => {
    if (!businessId.value) {
      log.warn('No business ID available for fetching info')
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await get(`/api/business/${businessId.value}`)
      businessInfo.value = response
      log.info('Business info fetched successfully')
    }
    catch (err: any) {
      error.value = err.message || 'Failed to fetch business info'
      log.error('Failed to fetch business info:', err)
    }
    finally {
      isLoading.value = false
    }
  }

  const updateBusinessInfo = (info: Partial<Business>): void => {
    if (businessInfo.value) {
      businessInfo.value = { ...businessInfo.value, ...info }
      log.info('📝 Business info updated locally')
    }
  }

  const initializeFromCookie = (): boolean => {
    log.debug('initializeFromCookie called - businessId from cookie:', businessId.value)
    return !!businessId.value
  }

  const ensureInitialized = async (): Promise<boolean> => {
    if (!businessId.value) {
      log.warn('❌ No business ID available for initialization')
      return false
    }

    // Always fetch business info if missing
    if (!businessInfo.value && !isLoading.value) {
      log.info('📡 Fetching missing businessInfo for businessId:', businessId.value)
      await fetchBusinessInfo()
    }

    return !!businessId.value && !!businessInfo.value
  }

  const setLoggingOut = (value: boolean): void => {
    isLoggingOut.value = value
    log.debug(`🚪 Logout state: ${value}`)
  }

  const reset = (): void => {
    log.info('🔄 Resetting business store')
    buuidCookie.value = null
    serviceTypeCookie.value = null
    businessInfo.value = null
    error.value = null
    isLoggingOut.value = false
    isLoading.value = false
    hasMultipleBusinesses.value = false
  }

  const clearBusinessData = (): void => {
    log.info('🧹 Clearing business data')
    buuidCookie.value = null
    serviceTypeCookie.value = null
    businessInfo.value = null
    error.value = null
    hasMultipleBusinesses.value = false
  }

  // Getters (computed properties for derived state)
  const hasBusinessId = computed(() => !!businessId.value)
  const businessName = computed(() => businessInfo.value?.name || '')
  const isReady = computed(() => !!businessId.value && !!businessInfo.value)

  // Auto-initialize: fetch businessInfo if businessId exists but businessInfo is missing
  // OR if service_type cookie doesn't match businessInfo.service_type
  const autoInitialize = async (): Promise<void> => {
    if (!import.meta.client || !businessId.value || isLoading.value) {
      return
    }

    // Check if businessInfo is missing
    const missingBusinessInfo = !businessInfo.value

    // Check if service_type cookie mismatches businessInfo
    const serviceTypeMismatch = businessInfo.value
      && serviceType.value
      && businessInfo.value.service_type !== serviceType.value

    if (missingBusinessInfo) {
      log.info('🔄 Auto-fetching businessInfo (missing) for businessId:', businessId.value)
      try {
        await fetchBusinessInfo()
        log.info('✅ Auto-fetch successful')
      }
      catch (err) {
        log.error('❌ Auto-fetch businessInfo failed:', err)
      }
    }
    else if (serviceTypeMismatch) {
      log.warn(
        `🔄 service_type mismatch detected! Cookie: ${serviceType.value}, Store: ${businessInfo.value.service_type} - re-fetching`,
      )
      try {
        await fetchBusinessInfo()
        log.info('✅ Re-fetch successful, data now consistent')
      }
      catch (err) {
        log.error('❌ Re-fetch businessInfo failed:', err)
      }
    }
  }

  // Trigger auto-initialization on store creation
  if (import.meta.client) {
    autoInitialize()
  }

  return {
    // State (read from cookies)
    businessId,
    serviceType,
    businessInfo,
    isLoading,
    error,
    isLoggingOut,
    hasMultipleBusinesses,

    // Getters
    hasBusinessId,
    businessName,
    isReady,

    // Actions
    setBusinessId,
    setLoggingOut,
    fetchBusinessInfo,
    updateBusinessInfo,
    initializeFromCookie,
    ensureInitialized,
    reset,
    clearBusinessData,
  }
}, {
  persist: {
    key: 'business-store',
    paths: ['businessInfo', 'hasMultipleBusinesses'],
  },
})
